/**
 * authFetch — drop-in replacement for `fetch` that:
 *   1. Attaches the current `accessToken` from localStorage as a Bearer token.
 *   2. On a 401 response, attempts a one-shot refresh via /api/accounts/refresh/.
 *   3. Retries the original request once with the new access token.
 *   4. If the refresh fails, clears stored tokens and notifies AuthContext via
 *      the same force-logout handler used by the central axios interceptor in
 *      `utils/api.js`, so ProtectedRoute can redirect to /login cleanly.
 *
 * This exists so legacy pages that still use raw `fetch` (e.g. estates, trust,
 * conveyancing) get the same automatic token-refresh behaviour as the axios
 * client, instead of immediately rendering a "Session Expired" screen the
 * moment the access token times out.
 */

// Shared force-logout handler registered by AuthContext via api.js.
// We import lazily to avoid a circular dependency at module load.
let getForceLogoutHandler = () => null;
try {
  // eslint-disable-next-line global-require
  const apiModule = require('./api');
  if (apiModule && typeof apiModule.getForceLogoutHandler === 'function') {
    getForceLogoutHandler = apiModule.getForceLogoutHandler;
  }
} catch (_e) {
  // ignore — api.js may not be available in some isolated test contexts
}

let refreshPromise = null;

const buildHeaders = (extra = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...extra,
  };
  const token = localStorage.getItem('accessToken');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const performRefresh = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('no_refresh_token');
  }

  const response = await fetch('/api/accounts/refresh/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  if (!response.ok) {
    throw new Error(`refresh_failed_${response.status}`);
  }

  const data = await response.json();
  const newAccess = data.access_token || data.access;
  const newRefresh = data.refresh_token || data.refresh;

  if (!newAccess) {
    throw new Error('refresh_no_access_token');
  }

  localStorage.setItem('accessToken', newAccess);
  if (newRefresh) {
    localStorage.setItem('refreshToken', newRefresh);
  }
  return newAccess;
};

const refreshAccessToken = () => {
  // De-duplicate concurrent refresh attempts.
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const handleRefreshFailure = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  const handler = getForceLogoutHandler();
  if (typeof handler === 'function') {
    try { handler(); } catch (_e) { /* ignore */ }
  }
};

/**
 * authFetch(input, init?) — same signature as window.fetch.
 *
 * `init.headers` are merged with auth + JSON defaults. Pass a string body or
 * stringify JSON yourself, exactly like native fetch.
 */
export const authFetch = async (input, init = {}) => {
  const { headers: customHeaders, _retry, ...rest } = init;
  const config = {
    ...rest,
    headers: buildHeaders(customHeaders),
  };

  let response = await fetch(input, config);

  // Don't try to refresh on the refresh endpoint itself.
  const isRefreshEndpoint = typeof input === 'string'
    && input.includes('/api/accounts/refresh');

  if (response.status === 401 && !_retry && !isRefreshEndpoint) {
    try {
      const newAccess = await refreshAccessToken();
      const retryHeaders = buildHeaders(customHeaders);
      retryHeaders['Authorization'] = `Bearer ${newAccess}`;
      response = await fetch(input, { ...rest, headers: retryHeaders });
    } catch (_err) {
      handleRefreshFailure();
      // Return the original 401 so callers can render their auth-error UI.
      return response;
    }
  }

  return response;
};

export default authFetch;
